import {
  create,
  compileDependencies,
  addDependencies,
  divideDependencies,
  multiplyDependencies,
  subtractDependencies,
  parseDependencies,
  randomIntDependencies,
  roundDependencies,
  formatDependencies,
  equalDependencies,
  numberDependencies,
  // eslint-disable-next-line import/extensions
} from 'mathjs/lib/browser/math.js';

import _ from 'lodash';

export default function createMathInstanceForQuestions() {
  return create(
    {
      addDependencies,
      divideDependencies,
      multiplyDependencies,
      subtractDependencies,
      compileDependencies,
      parseDependencies,
      randomIntDependencies,
      roundDependencies,
      formatDependencies,
      equalDependencies,
      numberDependencies,
    },
    { number: 'BigNumber' },
  );
}

export const replaceDecimalsSeparatorToComma = expression =>
  _.replace(expression, /\./g, ',');

export const replaceDecimalsSeparatorToDot = expression =>
  _.replace(expression, /,/g, '.');

export const replaceCurlyBrackets = expression =>
  _.replace(expression, /{|}/g, ' ');

export const substituteVariablesInQuestion = (question, variables) => {
  let questionWithSubstitutedVariables = question;
  _.each(variables, variable => {
    const regExp = new RegExp(`{${variable.name}}`, 'g');
    questionWithSubstitutedVariables = _.replace(
      questionWithSubstitutedVariables,
      regExp,
      replaceDecimalsSeparatorToComma(variable.generatedValue),
    );
  });
  return questionWithSubstitutedVariables;
};

export class BasicMathQuestionOperations {
  mathJsInstance;

  constructor() {
    this.mathJsInstance = createMathInstanceForQuestions();
  }

  generateRandomNumberInRangeWithStep = ({
    rangeMinValue,
    rangeMaxValue,
    step,
  }) => {
    if (!rangeMaxValue && rangeMaxValue !== 0) {
      return rangeMinValue;
    }
    const maxNumberOfSteppedValues = Math.floor(
      (rangeMaxValue - rangeMinValue) / step,
    );
    const randomStepNumber = this.mathJsInstance.randomInt(
      0,
      maxNumberOfSteppedValues + 1,
    );
    // TODO check do we need to include max range value in this range, or the last will be step value
    return this.mathJsInstance
      .parse('rangeMinValue + randomStepNumber * step')
      .evaluate({
        rangeMinValue: this.mathJsInstance.bignumber(rangeMinValue),
        randomStepNumber: this.mathJsInstance.bignumber(randomStepNumber),
        step: this.mathJsInstance.bignumber(step),
      });
  };

  generateStringQuestionExpressionAndVariables = (formula, mathVariables) => {
    // eslint-disable-next-line no-useless-catch
    try {
      const variablesValues = [];
      const formulaInstance = this.mathJsInstance.parse(
        replaceCurlyBrackets(formula),
      );
      const transformed = formulaInstance.transform(node => {
        if (node.isSymbolNode) {
          const variable = _.find(mathVariables, ['name', node.name]);
          if (!variable) {
            return node;
          }
          const generatedVariableValue = this.generateRandomNumberInRangeWithStep(
            {
              rangeMinValue: _.toNumber(variable.rangeMinValue),
              rangeMaxValue: _.toNumber(variable.rangeMaxValue),
              step: _.toNumber(variable.rangeStep),
            },
          );
          variablesValues.push({
            ...variable,
            generatedValue: generatedVariableValue,
          });
          return new this.mathJsInstance.ConstantNode(generatedVariableValue);
        }
        return node;
      });

      return {
        mathQuestionExpression: transformed.toString({
          implicit: 'show',
          parenthesis: 'keep',
        }),
        variablesValues,
      };
    } catch (e) {
      throw e;
    }
  };

  getStringEvaluatedExpressionAnswer = (
    mathQuestionExpression,
    allowedDecimals,
  ) => {
    const parsedExpression = this.mathJsInstance.parse(mathQuestionExpression);
    let correctAnswer = parsedExpression.evaluate();

    if (allowedDecimals === 'not allowed') {
      correctAnswer = this.mathJsInstance.round(correctAnswer);
    } else if (allowedDecimals !== 'no preference') {
      correctAnswer = this.mathJsInstance.round(
        correctAnswer,
        parseInt(allowedDecimals, 10),
      );
      correctAnswer = this.mathJsInstance.format(correctAnswer, {
        notation: 'fixed',
        precision: parseInt(allowedDecimals, 10),
      });
    }
    return correctAnswer.toString();
  };

  isEqualStringsAsNumbers = (str1, str2) => {
    const num1 = this.mathJsInstance.number(str1);
    const num2 = this.mathJsInstance.number(str2);
    return this.mathJsInstance.equal(num1, num2);
  };
}
